/**
 * Testimonial stories — easy-to-extend data structure.
 *
 * HOW TO ADD A NEW STORY:
 * 1) Upload the files to:
 *      /app/frontend/public/images/testimonials/<id>/   (images + video posters)
 *      /app/frontend/public/videos/testimonials/<id>/   (videos)
 *    Keep filenames in a numbered sequence (01, 02, …) to preserve order.
 * 2) Append a new object to PHOTO_STORIES below.
 * 3) The site auto-displays the new story in the grid, in the order
 *    defined by `order` (ascending). If `order` is omitted, the array
 *    order is used.
 *
 * SCHEMA:
 *   {
 *     id:         string   // short, URL-safe identifier (e.g. "monika")
 *     name:       string   // display name (e.g. "Mónika")
 *     title?:     string   // optional story title (defaults to "`${name} története`")
 *     subtitle?:  string   // optional short subline shown under the name in the card
 *     coverIndex?:number   // which item (0-based) to use as cover (default 0)
 *     items:      Array<MediaItem>
 *     order?:     number   // optional display order
 *   }
 *
 *   MediaItem = Image | Video
 *     Image = { type: "image", src: string, alt?: string }
 *     Video = {
 *       type: "video",
 *       src: string,            // .mp4 path
 *       poster: string,         // still frame used as cover / preview
 *       alt?: string,
 *       aspectRatio?: string    // e.g. "9 / 16" (used by the card poster frame)
 *     }
 *
 * NOTES:
 *   - Images and videos display at their original aspect ratio in the lightbox
 *     (`object-contain`); Facebook-screenshot text stays fully readable.
 *   - Videos do NOT autoplay in the lightbox. The viewer presses play when ready.
 *   - Do not reference external (Facebook) URLs — always host locally.
 */

export const PHOTO_STORIES = [
  {
    id: "monika",
    name: "Mónika",
    coverIndex: 0,
    order: 1,
    items: [
      {
        type: "video",
        src: "/videos/testimonials/monika/01.mp4",
        poster: "/images/testimonials/monika/01.jpg",
        alt: "Mónika visszajelzése — 1. rész",
        aspectRatio: "9 / 16",
      },
      {
        type: "video",
        src: "/videos/testimonials/monika/02.mp4",
        poster: "/images/testimonials/monika/02.jpg",
        alt: "Mónika visszajelzése — 2. rész",
        aspectRatio: "9 / 16",
      },
      {
        type: "video",
        src: "/videos/testimonials/monika/03.mp4",
        poster: "/images/testimonials/monika/03.jpg",
        alt: "Mónika visszajelzése — 3. rész",
        aspectRatio: "9 / 16",
      },
      {
        type: "video",
        src: "/videos/testimonials/monika/04.mp4",
        poster: "/images/testimonials/monika/04.jpg",
        alt: "Mónika visszajelzése — 4. rész",
        aspectRatio: "9 / 16",
      },
      {
        type: "video",
        src: "/videos/testimonials/monika/05.mp4",
        poster: "/images/testimonials/monika/05.jpg",
        alt: "Mónika visszajelzése — 5. rész",
        aspectRatio: "9 / 16",
      },
    ],
  },
  {
    id: "kriszti",
    name: "Kriszti",
    subtitle: "Svájc",
    coverIndex: 0,
    order: 2,
    items: [
      {
        type: "image",
        src: "/images/testimonials/kriszti/01.jpg",
        alt: "Kriszti visszajelzése — 1. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/kriszti/02.jpg",
        alt: "Kriszti visszajelzése — 2. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/kriszti/03.jpg",
        alt: "Kriszti visszajelzése — 3. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/kriszti/04.jpg",
        alt: "Kriszti visszajelzése — 4. kép",
      },
    ],
  },
  {
    id: "hajni",
    name: "Hajni",
    coverIndex: 0,
    order: 3,
    items: [
      {
        type: "image",
        src: "/images/testimonials/hajni/01.jpg",
        alt: "Hajni visszajelzése — 1. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/hajni/02.jpg",
        alt: "Hajni visszajelzése — 2. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/hajni/03.jpg",
        alt: "Hajni visszajelzése — 3. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/hajni/04.jpg",
        alt: "Hajni visszajelzése — 4. kép",
      },
    ],
  },
  {
    id: "nicoleta",
    name: "Nicoleta",
    coverIndex: 0,
    order: 4,
    items: [
      {
        type: "image",
        src: "/images/testimonials/nicoleta/01.jpg",
        alt: "Nicoleta visszajelzése — 1. kép",
      },
      {
        type: "image",
        src: "/images/testimonials/nicoleta/02.jpg",
        alt: "Nicoleta visszajelzése — 2. kép",
      },
    ],
  },
];

/** Sorted copy of PHOTO_STORIES by `order`, then array index. */
export const getSortedStories = () => {
  const withIndex = PHOTO_STORIES.map((s, i) => ({ s, i }));
  withIndex.sort((a, b) => {
    const ao = a.s.order ?? 1000 + a.i;
    const bo = b.s.order ?? 1000 + b.i;
    return ao - bo;
  });
  return withIndex.map(({ s }) => s);
};
